/**
 * Public-site notifications. Every title and body is the return value of the
 * function the API uses when it sends that push or writes that in-app row.
 * People and the club are fictional. Sources are listed on each sample.
 */
import {
  bookedInPushCopy,
  buildSpotsLeftNotice,
  catalogReminderCopy,
  feeAlertAmountLine,
  feeAlertHeading,
  feeFollowUpAmount,
  feeFollowUpCopy,
  programWeekCopy,
  sessionListPhrase,
  swapNoticeBody,
  swapNoticeTitle,
} from '@marshal/shared';

const CLUB = 'Harbourside Swim Club';
const SWIMMER = 'Cameron Kline';
const SQUAD = 'Tadpoles';
const POOL = 'Civic Pool';
const SESSION_DATE = '2026-10-27';
const START = '17:30:00';
const WEEK = '2026-10-26';

export type NoticeSample = {
  id: string;
  title: string;
  body: string;
  source: string;
  spots?: boolean;
};

const phrase = sessionListPhrase([
  { squadName: SQUAD, sessionDate: SESSION_DATE, startTime: START },
]);

const booking = bookedInPushCopy({
  swimmerName: SWIMMER,
  squadName: SQUAD,
  sessionDate: SESSION_DATE,
  startTime: START,
  placementStatus: 'booked',
});

const spots = buildSpotsLeftNotice({
  clubName: CLUB,
  squadName: SQUAD,
  sessionDate: SESSION_DATE,
  startTime: START,
  poolName: POOL,
  threshold: 3,
  swimmerNames: [SWIMMER],
  bookUrl: '/sessions/sample',
});

const requested = {
  title: swapNoticeTitle('requested'),
  body: swapNoticeBody({
    event: 'requested',
    role: 'new',
    actorName: 'Blake Hale',
    phrase,
    fromName: 'Blake Hale',
    toName: 'Reese Alden',
  }),
};

const accepted = {
  title: swapNoticeTitle('accepted'),
  body: swapNoticeBody({
    event: 'accepted',
    role: 'original',
    actorName: 'Reese Alden',
    phrase,
    fromName: 'Blake Hale',
    toName: 'Reese Alden',
  }),
};

const feePush = feeFollowUpCopy({
  clubName: CLUB,
  overdueCount: 1,
  amountLabel: feeFollowUpAmount(90),
});

const program = programWeekCopy({
  squadName: SQUAD,
  weekStarting: WEEK,
  reason: 'publish',
});

const catalog = catalogReminderCopy('pink-shirt-day', 'week', 'parent');
if (!catalog) {
  throw new Error('Pink Shirt Day reminder template is missing');
}

const feeHeading = feeAlertHeading({
  status: 'overdue',
  kind: 'membership',
  label: 'Invoice INV-1042',
});
const feeAmount = feeAlertAmountLine({ amount_due: 90, due_date: '2026-10-01' });
if (!feeAmount) {
  throw new Error('Fee alert amount line is missing');
}

export const heroPushes: readonly NoticeSample[] = [
  {
    id: 'booking',
    title: booking.title,
    body: booking.body,
    source: 'bookedInPushCopy, sent by coachCheckInNotify',
  },
  {
    id: 'spots',
    title: spots.title,
    body: spots.body,
    source: 'buildSpotsLeftNotice, sent by sessionCapacityNotify',
    spots: true,
  },
];

export const draftPushes: readonly NoticeSample[] = [
  {
    id: 'swap-requested',
    title: requested.title,
    body: requested.body,
    source: 'swapNoticeTitle and swapNoticeBody, sent by coachSessionSwapNotify',
  },
  {
    id: 'swap-accepted',
    title: accepted.title,
    body: accepted.body,
    source: 'swapNoticeTitle and swapNoticeBody, sent by coachSessionSwapNotify',
  },
];

export const feeCard = {
  title: feeHeading,
  body: feeAmount,
  source: 'feeAlertHeading and feeAlertAmountLine, rendered by HomeAlerts',
};

export const inAppRows: readonly NoticeSample[] = [
  {
    id: 'fee-push',
    title: feePush.title,
    body: feePush.body,
    source: 'feeFollowUpCopy, sent by the treasurer follow-up in finance.ts',
  },
  {
    id: 'spots-row',
    title: spots.title,
    body: spots.body,
    source: 'buildSpotsLeftNotice, parent home Notices row',
    spots: true,
  },
  {
    id: 'program',
    title: program.title,
    body: program.body,
    source: 'programWeekCopy, sent by programCoachNotify',
  },
  {
    id: 'notice',
    title: catalog.title,
    body: catalog.body,
    source: 'catalogReminderCopy for pink-shirt-day, sent by calendar reminders',
  },
];
